import Banner from 'components/molecules/Banner';
import useFetchAccessRequests from 'hooks/Access';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import {
  acceptInvitation,
  declineInvitation,
  getClientWorkspacesCategorized,
} from 'api/v3/access/clients';
import { CheckIconOutline, XIconOutline } from 'utils/icons';
import { Button } from 'components/atoms/Button';
import { Loader } from 'components/atoms/Loader';
import classNames from 'classnames';
import { UserCacheKeys } from 'types/cacheKeys.types';

const PendingAccessRequestsBanner: FC = () => {
  const { t } = useTranslation();
  const pendingAccessAPI = useFetchAccessRequests();
  const workspacesApi = useQuery(
    [UserCacheKeys.UserWorkspaces],
    getClientWorkspacesCategorized,
  );

  const acceptInvitationAPI = useMutation(acceptInvitation, {
    onSuccess: () => {
      pendingAccessAPI.refetch();
      workspacesApi.refetch();
    },
  });

  const declineInvitationAPI = useMutation(declineInvitation, {
    onSuccess: () => {
      pendingAccessAPI.refetch();
    },
  });

  // Don't render the banner if there are no pending requests
  if (!pendingAccessAPI.data?.length) {
    return null;
  }

  const btnClass =
    'flex items-center gap-1 py-2 rounded-full text-sm font-semibold';

  return (
    <>
      {pendingAccessAPI.data.map((invitation) => (
        <Banner
          key={invitation._id}
          bannerWrapperClassName="[&>*:first-child]:flex [&>*:first-child]:flex-col [&>*:first-child]:items-start"
          title={t('data_access.request.title', {
            email: invitation.from,
          })}
          description={t('data_access.request.description')}
          extra={
            <div className="flex gap-6 mt-4">
              <Button
                structure="text"
                color="taxes"
                disabled={acceptInvitationAPI.isLoading}
                onClick={() => {
                  acceptInvitationAPI.mutate(invitation.token);
                }}
                className={classNames(btnClass)}
              >
                {acceptInvitationAPI.isLoading ? (
                  <Loader />
                ) : (
                  <CheckIconOutline className="w-5 h-5" />
                )}
                {t('data_access.button.accept')}
              </Button>
              <Button
                structure="text"
                color="red"
                disabled={declineInvitationAPI.isLoading}
                onClick={() => {
                  declineInvitationAPI.mutate(invitation.token);
                }}
                className={classNames(btnClass)}
              >
                {declineInvitationAPI.isLoading ? (
                  <Loader />
                ) : (
                  <XIconOutline className="w-5 h-5" />
                )}
                {t('data_access.button.decline')}
              </Button>
            </div>
          }
        />
      ))}
    </>
  );
};

export default PendingAccessRequestsBanner;
